<template>
  <AiEducationLayout>
    <EducationPageHeader title="教研员工作台" description="审核当前授权课程范围内的教学内容草稿" />
    <ContentWrap>
      <div class="toolbar"
        ><el-select v-model="statusFilter" clearable placeholder="全部状态" @change="load"
          ><el-option
            v-for="item in statuses"
            :key="item.value"
            :label="item.label"
            :value="item.value" /></el-select
        ><el-button @click="load">刷新</el-button></div
      >
      <EducationState v-if="state === 'loading'" state="loading" title="正在加载内容草稿" />
      <EducationState
        v-else-if="state === 'error'"
        state="error"
        title="内容草稿加载失败"
        description="请确认当前教研员课程范围和查询权限。"
        @retry="load"
      />
      <EducationState
        v-else-if="!drafts.length"
        state="empty"
        title="暂无待处理内容"
        description="教师提交内容草稿后会显示在这里。"
      />
      <el-table v-else :data="drafts" row-key="id" stripe>
        <el-table-column prop="title" label="内容标题" min-width="220" />
        <el-table-column prop="trainCourseId" label="课程 ID" width="110" />
        <el-table-column label="状态" width="110"
          ><template #default="{ row }"
            ><el-tag>{{ statusLabel(row.status) }}</el-tag></template
          ></el-table-column
        >
        <el-table-column prop="generatedTime" label="生成时间" width="180" />
        <el-table-column label="操作" width="230" fixed="right"
          ><template #default="{ row }"
            ><el-button link @click="openDetail(row)">详情</el-button
            ><el-button v-if="row.status === 10" link type="success" @click="review(row.id, true)"
              >通过</el-button
            ><el-button v-if="row.status === 10" link type="danger" @click="review(row.id, false)"
              >驳回</el-button
            ><span v-else>—</span></template
          ></el-table-column
        >
      </el-table>
    </ContentWrap>
    <el-drawer v-model="detailVisible" title="内容草稿详情" size="520px">
      <template v-if="selectedDraft">
        <h3>{{ selectedDraft.title }}</h3>
        <p class="draft-content">{{ selectedDraft.content || '暂无草稿正文' }}</p>
        <el-descriptions :column="1" border>
          <el-descriptions-item label="课程 ID">{{
            selectedDraft.trainCourseId
          }}</el-descriptions-item>
          <el-descriptions-item label="内容类型">{{
            selectedDraft.contentType
          }}</el-descriptions-item>
          <el-descriptions-item label="证据">{{
            selectedDraft.evidenceJson || '暂无证据字段'
          }}</el-descriptions-item>
        </el-descriptions>
        <h4>审核记录</h4>
        <EducationState v-if="reviewsLoading" state="loading" title="正在加载审核记录" />
        <EducationState v-else-if="!reviews.length" state="empty" title="暂无审核记录" />
        <el-timeline v-else
          ><el-timeline-item v-for="reviewItem in reviews" :key="reviewItem.id"
            ><strong>{{ actionLabel(reviewItem.action) }}</strong
            ><p>{{ reviewItem.comment || '无审核意见' }}</p></el-timeline-item
          ></el-timeline
        >
      </template>
    </el-drawer>
  </AiEducationLayout>
</template>

<script setup lang="ts">
import { onMounted, ref } from 'vue'
import AiEducationLayout from '../layout/AiEducationLayout.vue'
import EducationPageHeader from '../components/EducationPageHeader.vue'
import EducationState from '../components/EducationState.vue'
import {
  getContentDraftList,
  getContentReviewList,
  reviewContentDraft,
  type ContentDraftVO
} from '@/api/aiEducation'

defineOptions({ name: 'AiEducationResearcherHome' })
const drafts = ref<ContentDraftVO[]>([])
const state = ref<'loading' | 'ready' | 'error'>('loading')
const statusFilter = ref<number | undefined>(10)
const statuses = [
  { label: '待审核', value: 10 },
  { label: '已通过', value: 20 },
  { label: '已驳回', value: 30 },
  { label: '已发布', value: 40 }
]
const detailVisible = ref(false)
const selectedDraft = ref<ContentDraftVO>()
const reviews = ref<any[]>([])
const reviewsLoading = ref(false)
const statusLabel = (status?: number) =>
  statuses.find((item) => item.value === status)?.label || '草稿'
const load = async () => {
  state.value = 'loading'
  try {
    drafts.value = (await getContentDraftList({ status: statusFilter.value })) || []
    state.value = 'ready'
  } catch {
    state.value = 'error'
  }
}
const review = async (draftId: number, approved: boolean) => {
  try {
    await reviewContentDraft({
      draftId,
      approved,
      comment: approved ? '教研审核通过' : '教研审核驳回，请修改后重新提交'
    })
    ElMessage.success(approved ? '审核已通过' : '已驳回')
    await load()
  } catch {
    ElMessage.error('审核操作失败')
  }
}
const openDetail = async (draft: ContentDraftVO) => {
  selectedDraft.value = draft
  detailVisible.value = true
  reviewsLoading.value = true
  try {
    reviews.value = (await getContentReviewList(draft.id!)) || []
  } catch {
    reviews.value = []
  } finally {
    reviewsLoading.value = false
  }
}
const actionLabel = (action?: number) =>
  action === 20 ? '通过' : action === 30 ? '驳回' : action === 40 ? '发布' : '审核'
onMounted(load)
</script>

<style scoped>
.toolbar {
  display: flex;
  gap: 10px;
  margin-bottom: 16px;
}
.toolbar .el-select {
  width: 180px;
}
.draft-content {
  white-space: pre-wrap;
  line-height: 1.7;
  color: #47535d;
}
</style>
